import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { KpiCard } from '@/components/shared/KpiCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Building2, Users, ClipboardList, FileText, Plus, ArrowRight, ChevronRight,
  AlertTriangle, Clock, Flame, Hourglass, Inbox, CheckCircle2, Bell, Loader2,
  MoreVertical, EyeOff,
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { NewEvaluationDialog } from '@/components/admin/NewEvaluationDialog';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/adminApi';

const KpiLink = ({ to, testId, children }) => (
  <Link
    to={to}
    data-testid={testId}
    className="block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-smooth hover:-translate-y-0.5 cursor-pointer"
  >
    {children}
  </Link>
);

const URGENCY_TONE = {
  high: 'bg-destructive/10 text-destructive border-destructive/30',
  urgent: 'bg-destructive/10 text-destructive border-destructive/30',
  critical: 'bg-destructive/10 text-destructive border-destructive/30',
  medium: 'bg-warning/10 text-warning border-warning/30',
  low: 'bg-muted text-muted-foreground border-border',
};

const WAITING_TONE = {
  advisor: 'bg-primary/10 text-primary border-primary/30',
  hm_admin: 'bg-accent/15 text-accent-foreground border-accent/30',
  client: 'bg-muted text-muted-foreground border-border',
};

const SUITE_LINK = {
  talentmap: '/admin/talentmap',
  talentcheck: '/admin/talentcheck',
  talentcompetency: '/admin/talentcompetency/requests',
  talenttrain: '/admin/talenttrain',
  talentfix: '/admin/talentfix',
  climara: '/admin/climara',
  talentstart: '/admin/talentstart',
  talentheart: '/admin/talentheart',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(getUiLocale()) : '—');

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const isHmAdmin = user?.role === 'hm_admin';
  const [evalOpen, setEvalOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissedOpen, setDismissedOpen] = useState(false);
  const [dismissedItems, setDismissedItems] = useState([]);

  const loadDash = () => adminApi.getOperationsDashboard().then(setData).catch(() => setData(null));

  useEffect(() => {
    adminApi.getOperationsDashboard().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  // CTH-DASH-084 — manual priority management
  const dismissPriority = async (p, snoozeDays = null) => {
    let reason = null;
    if (!snoozeDays) {
      reason = window.prompt(t('pages.dashboard.ops.dismissReason', { defaultValue: 'Motivo (opcional):' })) || null;
    }
    try {
      await api.post('/admin/dashboard/priorities/dismiss', {
        type: p.type, id: p.id, reason, snooze_days: snoozeDays,
        fingerprint: p.fingerprint || null, title: p.title || priTypeLabel(p),
      });
      toast.success(snoozeDays
        ? t('pages.dashboard.ops.snoozed', { defaultValue: `Pospuesta ${snoozeDays} día(s)` })
        : t('pages.dashboard.ops.dismissed', { defaultValue: 'Descartada del panel (el registro original no se modifica)' }));
      loadDash();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDismissed = async () => {
    try {
      const r = await api.get('/admin/dashboard/priorities/dismissed');
      setDismissedItems(r.data.items || []);
      setDismissedOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreDismissed = async (d) => {
    try {
      await api.post(`/admin/dashboard/priorities/${d.id}/restore`);
      setDismissedItems((xs) => xs.filter((x) => x.id !== d.id));
      toast.success(t('pages.dashboard.ops.restored', { defaultValue: 'Prioridad restaurada' }));
      loadDash();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const priTypeLabel = (p) => t(`pages.dashboard.ops.type.${p.type}`, { defaultValue: p.type });
  const trend = data?.trend || [];
  const trendMeaningful = trend.filter((m) => m.created > 0).length >= 3
    && trend.reduce((a, m) => a + m.created, 0) >= 10;

  if (loading) {
    return (
      <>
        <Topbar title={t('pages.dashboard.title')} subtitle={t('pages.dashboard.subtitle')} />
        <div className="p-10 flex items-center justify-center text-muted-foreground" data-testid="ops-dashboard-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  const k = data?.kpis || {};
  const priorities = data?.priorities || [];
  const ind = data?.indicators || {};

  return (
    <>
      <Topbar
        title={t('pages.dashboard.title')}
        subtitle={t('pages.dashboard.opsSubtitle', { defaultValue: 'Operational command center — what needs your attention today' })}
        actions={
          <Button
            data-testid="dashboard-new-eval-btn"
            onClick={() => setEvalOpen(true)}
            className="hidden md:inline-flex bg-gradient-primary hover:opacity-90 font-medium gap-1.5"
          >
            <Plus className="h-4 w-4" /> {t('pages.dashboard.newEvaluation')}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="ops-dashboard">
        {/* Executive KPIs — kept, now live data */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiLink to="/admin/clients?status=active" testId="kpi-active-clients">
            <KpiCard label={t('pages.dashboard.kpiActiveClients')} value={k.active_clients ?? '—'} icon={Building2} tone="primary" />
          </KpiLink>
          <KpiLink to="/admin/users" testId="kpi-total-people">
            <KpiCard label={t('pages.dashboard.kpiTotalPeople')} value={(k.registered_people ?? 0).toLocaleString(getUiLocale())} icon={Users} tone="info" />
          </KpiLink>
          <KpiLink to="/admin/evaluations?status=active" testId="kpi-active-evaluations">
            <KpiCard label={t('pages.dashboard.kpiActiveEvaluations')} value={k.active_evaluations ?? '—'} icon={ClipboardList} tone="accent" />
          </KpiLink>
          <KpiLink to="/admin/reports" testId="kpi-reports-month">
            <KpiCard label={t('pages.dashboard.ops.kpiReportsIssued', { defaultValue: 'Reports issued' })} value={k.reports_issued ?? '—'} icon={FileText} tone="success" />
          </KpiLink>
        </div>

        {/* Actionable indicators strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ops-indicators">
          {[
            { key: 'high_priority', icon: Flame, tone: 'text-destructive', link: '/admin/leads' },
            { key: 'aging_7d', icon: Hourglass, tone: 'text-warning', link: '/admin/leads' },
            { key: 'not_answered', icon: Clock, tone: 'text-info', link: '/admin/evaluations?status=active' },
            { key: 'waiting_admin', icon: Inbox, tone: 'text-accent-foreground', link: '/admin/leads' },
            { key: 'waiting_client', icon: Users, tone: 'text-muted-foreground', link: '/admin/evaluations?status=active' },
          ].map(({ key, icon: Icon, tone, link }) => (
            <Link key={key} to={link} data-testid={`ops-indicator-${key}`}
                  className="rounded-lg border border-border bg-card px-3 py-2.5 flex items-center gap-2.5 hover:bg-muted/50 transition-smooth">
              <Icon className={`h-4 w-4 shrink-0 ${tone}`} />
              <div className="min-w-0">
                <p className="text-lg font-display font-bold leading-none tabular-nums">{ind[key] ?? 0}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {t(`pages.dashboard.ops.ind.${key}`, { defaultValue: key })}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* CENTERPIECE — Today's priorities */}
        <Card className="p-6 shadow-card border-primary/20" data-testid="ops-priorities">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                <AlertTriangle className="h-4.5 w-4.5 h-5 w-5 text-primary" />
                {t('pages.dashboard.ops.prioritiesTitle', { defaultValue: "Today's priorities" })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('pages.dashboard.ops.prioritiesSub', { defaultValue: 'Everything waiting for a decision, most urgent first' })}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {isHmAdmin && (data?.dismissed_count || 0) > 0 && (
                <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={openDismissed} data-testid="ops-priorities-dismissed-btn">
                  <EyeOff className="h-3.5 w-3.5" /> {data.dismissed_count}
                </Button>
              )}
              <Link to="/admin/leads">
                <Button variant="ghost" size="sm" className="gap-1 text-primary hover:text-primary" data-testid="ops-priorities-viewall">
                  {t('pages.dashboard.viewAll')} <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </div>
          {priorities.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="ops-priorities-empty">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success" />
              <p className="text-sm font-medium">{t('pages.dashboard.ops.allClear', { defaultValue: 'All clear — nothing is waiting on you' })}</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {priorities.map((p) => (
                <div key={`${p.type}-${p.id}`} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => navigate(p.link)}
                  data-testid={`ops-priority-${p.id}`}
                  className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border/60 hover:bg-muted/50 hover:border-primary/40 transition-smooth text-left"
                >
                  <Badge variant="outline" className={`text-xs shrink-0 ${URGENCY_TONE[p.urgency] || URGENCY_TONE.medium}`}>
                    {t(`pages.requests.priority.${p.urgency}`, { defaultValue: p.urgency })}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {priTypeLabel(p)}
                      {p.type === 'reports_review'
                        ? <span className="text-muted-foreground font-normal"> · {t('pages.dashboard.ops.reportsCount', { count: p.count, defaultValue: '{{count}} report(s)' })}</span>
                        : p.title ? <span className="text-muted-foreground font-normal"> · {p.title}</span> : null}
                    </p>
                    {p.company_name && <p className="text-xs text-muted-foreground truncate">{p.company_name}</p>}
                  </div>
                  {p.age_days > 7 && (
                    <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/30 shrink-0" data-testid={`ops-priority-overdue-${p.id}`}>
                      {t('pages.dashboard.ops.overdue', { days: p.age_days, defaultValue: '{{days}}d old' })}
                    </Badge>
                  )}
                  {p.age_days > 0 && p.age_days <= 7 && (
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">{p.age_days}d</span>
                  )}
                  <Badge variant="outline" className={`text-xs shrink-0 hidden sm:inline-flex ${WAITING_TONE[p.waiting_on] || ''}`}>
                    {t(`pages.dashboard.ops.waiting.${p.waiting_on}`, { defaultValue: p.waiting_on })}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
                {isHmAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" data-testid={`ops-priority-menu-${p.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(p.link)} data-testid={`ops-priority-open-${p.id}`}>
                        {t('pages.dashboard.ops.openRecord', { defaultValue: 'Abrir registro' })}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => dismissPriority(p, 7)} data-testid={`ops-priority-snooze-${p.id}`}>
                        {t('pages.dashboard.ops.snooze7', { defaultValue: 'Posponer 7 días' })}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => dismissPriority(p, 30)}>
                        {t('pages.dashboard.ops.snooze30', { defaultValue: 'Posponer 30 días' })}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-rose-600" onClick={() => dismissPriority(p)} data-testid={`ops-priority-dismiss-${p.id}`}>
                        {t('pages.dashboard.ops.dismiss', { defaultValue: 'Descartar del panel' })}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Workload by client + by suite */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 shadow-card" data-testid="ops-by-client">
            <h3 className="font-display font-bold text-lg tracking-tight mb-1">
              {t('pages.dashboard.ops.byClientTitle', { defaultValue: 'Workload by client' })}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('pages.dashboard.ops.byClientSub', { defaultValue: 'Which client needs attention first' })}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pages.dashboard.ops.thClient', { defaultValue: 'Client' })}</TableHead>
                  <TableHead className="text-center">{t('pages.dashboard.ops.thPending', { defaultValue: 'Pending' })}</TableHead>
                  <TableHead className="text-center hidden md:table-cell">{t('pages.dashboard.ops.thActive', { defaultValue: 'Active evals' })}</TableHead>
                  <TableHead className="text-center hidden md:table-cell">{t('pages.dashboard.ops.thReports', { defaultValue: 'Reports in review' })}</TableHead>
                  <TableHead className="text-center hidden lg:table-cell">{t('pages.dashboard.ops.thCases', { defaultValue: 'Open cases' })}</TableHead>
                  <TableHead className="text-right hidden lg:table-cell">{t('pages.dashboard.ops.thLastActivity', { defaultValue: 'Last activity' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.by_client || []).map((c) => (
                  <TableRow key={c.company_id} className="cursor-pointer hover:bg-muted/40"
                            onClick={() => navigate(`/admin/clients/${c.company_id}`)}
                            data-testid={`ops-client-row-${c.company_id}`}>
                    <TableCell className="font-medium truncate max-w-[220px]" title={c.company_name}>{c.company_name}</TableCell>
                    <TableCell
                      className="text-center"
                      onClick={(ev) => {
                        if (c.pending_requests > 0) {
                          ev.stopPropagation();
                          navigate(`/admin/leads?company_id=${c.company_id}&status=pending`);
                        }
                      }}
                      data-testid={`ops-client-pending-${c.company_id}`}
                      title={c.pending_requests > 0 ? t('pages.dashboard.ops.pendingClickHint', { defaultValue: 'View pending items in the Approval Center' }) : undefined}
                    >
                      {c.pending_requests > 0
                        ? <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 tabular-nums cursor-pointer hover:bg-warning/25 underline decoration-dotted underline-offset-2">{c.pending_requests}</Badge>
                        : <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-center tabular-nums hidden md:table-cell">{c.active_evaluations}</TableCell>
                    <TableCell className="text-center tabular-nums hidden md:table-cell">{c.pending_reports}</TableCell>
                    <TableCell className="text-center tabular-nums hidden lg:table-cell">{c.open_cases}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground hidden lg:table-cell">{fmtDate(c.last_activity)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card className="p-6 shadow-card" data-testid="ops-by-suite">
            <h3 className="font-display font-bold text-lg tracking-tight mb-1">
              {t('pages.dashboard.ops.bySuiteTitle', { defaultValue: 'Workload by suite' })}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('pages.dashboard.ops.bySuiteSub', { defaultValue: 'Pending work, not just usage' })}
            </p>
            <div className="space-y-1.5">
              {(data?.by_suite || []).map((s) => (
                <Link key={s.suite_code} to={SUITE_LINK[s.suite_code] || '/admin/leads'}
                      data-testid={`ops-suite-${s.suite_code}`}
                      className="flex items-center justify-between px-3 py-2 rounded-lg border border-border/60 hover:bg-muted/50 transition-smooth">
                  <span className="text-sm font-medium capitalize">{t(`suites.${s.suite_code}.name`, { defaultValue: s.suite_code })}</span>
                  <span className="flex items-center gap-2">
                    {s.pending > 0 && (
                      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-xs tabular-nums">
                        {s.pending} {t('pages.dashboard.ops.pendingShort', { defaultValue: 'pending' })}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {s.active} {t('pages.dashboard.ops.activeShort', { defaultValue: 'active' })}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/* Recent activity — real notifications */}
        <Card className="p-6 shadow-card" data-testid="ops-recent-activity">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">{t('pages.dashboard.recentActivity')}</h3>
              <p className="text-sm text-muted-foreground">{t('pages.dashboard.ops.recentSub', { defaultValue: 'Latest events across your clients' })}</p>
            </div>
            <Link to="/admin/audit">
              <Button variant="ghost" size="sm" className="gap-1 text-primary hover:text-primary">
                {t('pages.dashboard.viewAuditLog')} <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
          {(data?.recent_activity || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('pages.dashboard.ops.noActivity', { defaultValue: 'No recent activity yet.' })}</p>
          ) : (
            <div className="divide-y divide-border">
              {(data?.recent_activity || []).map((n) => (
                <button key={n.id} type="button" onClick={() => n.link && navigate(n.link)}
                        data-testid={`ops-activity-${n.id}`}
                        className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-muted/40 rounded-md px-2 transition-smooth">
                  <Bell className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <p className="flex-1 text-sm truncate">{i18n.language === 'en' ? (n.title_en || n.title) : (n.title || n.title_en)}</p>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">{fmtDate(n.created_at)}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Secondary real-data trend — hidden unless meaningful */}
        {trendMeaningful && (
          <Card className="p-6 shadow-card" data-testid="ops-trend">
            <h3 className="font-display font-bold text-lg tracking-tight mb-1">{t('pages.dashboard.evaluationsOverTime')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('pages.dashboard.ops.trendSub', { defaultValue: 'Real assignment history (last 6 months)' })}</p>
            <div className="h-52" style={{ minHeight: 208 }}>
              <ResponsiveContainer width="100%" height={208}>
                <AreaChart data={trend} margin={{ left: -15, right: 4, top: 5, bottom: 0 }}>
                  <defs>
                    <linearGradient id="og1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} /></linearGradient>
                    <linearGradient id="og2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--chart-2))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="created" name={t('pages.dashboard.ops.trendCreated', { defaultValue: 'Assigned' })} stroke="hsl(var(--chart-1))" fill="url(#og1)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="completed" name={t('pages.dashboard.completed')} stroke="hsl(var(--chart-2))" fill="url(#og2)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>

      <NewEvaluationDialog open={evalOpen} onOpenChange={setEvalOpen} />
      <Dialog open={dismissedOpen} onOpenChange={setDismissedOpen}>
        <DialogContent className="max-w-md" data-testid="dismissed-priorities-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.dashboard.ops.dismissedTitle', { defaultValue: 'Prioridades descartadas / pospuestas' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {dismissedItems.length === 0 && <p className="text-sm text-muted-foreground">{t('common.empty', { defaultValue: 'Sin elementos' })}</p>}
            {dismissedItems.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-md border p-2.5 text-sm" data-testid={`dismissed-item-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{d.title || d.key}</p>
                  <p className="text-xs text-muted-foreground">
                    {(d.created_at || '').slice(0, 10)}
                    {d.snooze_until ? ` · ${t('pages.dashboard.ops.until', { defaultValue: 'hasta' })} ${(d.snooze_until || '').slice(0, 10)}` : ''}
                    {d.reason ? ` · ${d.reason}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => restoreDismissed(d)} data-testid={`dismissed-restore-${d.id}`}>
                  {t('pages.dashboard.ops.restore', { defaultValue: 'Restaurar' })}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
